"use client";

import React, { useEffect, useState, useRef, useId } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Upload, CircleCheck as CheckCircle2, CircleAlert as AlertCircle, FileText, X, Loader as Loader2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
interface UploadedFileItem {
  fieldId: string;
  fileId: string;
  name: string;
  size: number;
}
export default function QuoteForm() {
  const [formData, setFormData] = useState<any>(null);
  const [loadingForm, setLoadingForm] = useState(true);
  const [formLoadError, setFormLoadError] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, any>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, {
    code?: string;
    message: string;
  }>>({});
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmationHtml, setConfirmationHtml] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFileItem[]>([]);
  const [uploadingField, setUploadingField] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [honeypotVal, setHoneypotVal] = useState("");
  const fetchTimeRef = useRef<number>(Date.now());
  const prefersReduced = useReducedMotion();
  const nameInputId = useId();
  const companyInputId = useId();
  const emailInputId = useId();
  const phoneInputId = useId();
  const locationInputId = useId();
  const projectTypeSelectId = useId();
  const startDateInputId = useId();
  const detailsTextareaId = useId();
  const preferredContactSelectId = useId();
  const photoUploadId = useId();
  const docUploadId = useId();
  const loadFormDefinition = async () => {
    setLoadingForm(true);
    setFormLoadError(null);
    try {
      const client = getWvcClient();
      if (!client) {
        throw new Error("Form client unavailable");
      }
      let res: any;
      try {
        res = await client.pluginRequest("wvc-forms/v1/forms/quote-request-form");
      } catch (err: any) {
        if (err?.status === 404) {
          const listRes = await client.pluginRequest("wvc-forms/v1/forms");
          const items = listRes?.items || [];
          const matched = items.find((item: any) => item.key === "quote-request-form") || items.find((item: any) => item.name?.toLowerCase().includes("quote") || item.name?.toLowerCase().includes("scaffolding")) || (items.length === 1 ? items[0] : null);
          if (matched) {
            res = await client.pluginRequest(`wvc-forms/v1/forms/${matched.key}`);
          } else {
            throw new Error("Quote form record not found");
          }
        } else {
          throw err;
        }
      }
      if (res && res.key) {
        setFormData(res);
        fetchTimeRef.current = Date.now();
        const initialVals: Record<string, any> = {};
        if (Array.isArray(res.fields)) {
          res.fields.forEach((f: any) => {
            if (f.default !== undefined && f.default !== null) {
              initialVals[f.id] = f.default;
            } else {
              initialVals[f.id] = f.type === "checkbox" ? [] : "";
            }
          });
        }
        setValues(initialVals);
      } else {
        throw new Error("Invalid form schema received");
      }
    } catch (err: any) {
      setFormLoadError(err?.message || "Failed to load quote request form");
    } finally {
      setLoadingForm(false);
    }
  };
  useEffect(() => {
    loadFormDefinition();
  }, []);
  const getField = (fieldKey: string) => {
    if (!formData?.fields) return null;
    return formData.fields.find((f: any) => f.key === fieldKey) || null;
  };
  const handleInputChange = (fieldId: string, val: any) => {
    setValues(prev => ({
      ...prev,
      [fieldId]: val
    }));
    if (fieldErrors[fieldId]) {
      setFieldErrors(prev => {
        const next = {
          ...prev
        };
        delete next[fieldId];
        return next;
      });
    }
    if (globalError) setGlobalError(null);
  };
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, fieldId: string) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const client = getWvcClient();
    if (!client?.pluginUpload) {
      setUploadError("File upload is currently not supported in this environment.");
      return;
    }
    setUploadingField(fieldId);
    setUploadError(null);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const res = await client.pluginUpload(`wvc-forms/v1/forms/${formData.key}/upload`, {
          file,
          fields: {
            token: formData.spam?.token,
            field: fieldId
          }
        });
        if (res?.success && res.file_id) {
          const newItem: UploadedFileItem = {
            fieldId,
            fileId: res.file_id,
            name: res.name || file.name,
            size: res.size || file.size
          };
          setUploadedFiles(prev => [...prev, newItem]);
          setValues(prev => {
            const currentArr = Array.isArray(prev[fieldId]) ? prev[fieldId] : [];
            return {
              ...prev,
              [fieldId]: [...currentArr, res.file_id]
            };
          });
        } else {
          setUploadError(res?.message || "File upload failed. Please verify file format and size.");
        }
      }
    } catch (err: any) {
      setUploadError(err?.message || "An error occurred while uploading your file.");
    } finally {
      setUploadingField(null);
      e.target.value = "";
    }
  };
  const handleRemoveFile = (fieldId: string, fileId: string) => {
    setUploadedFiles(prev => prev.filter(f => f.fileId !== fileId));
    setValues(prev => {
      const currentArr = Array.isArray(prev[fieldId]) ? prev[fieldId] : [];
      return {
        ...prev,
        [fieldId]: currentArr.filter((id: string) => id !== fileId)
      };
    });
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || !formData) return;
    setGlobalError(null);
    setFieldErrors({});
    const minSecs = formData.spam?.min_seconds || 3;
    const elapsedSecs = (Date.now() - fetchTimeRef.current) / 1000;
    if (elapsedSecs < minSecs) {
      const waitTime = Math.ceil((minSecs - elapsedSecs) * 1000);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
    setSubmitting(true);
    try {
      const client = getWvcClient();
      if (!client?.pluginRequest) {
        throw new Error("Form submission client unavailable");
      }
      const cleanValues: Record<string, any> = {};
      if (Array.isArray(formData.fields)) {
        formData.fields.forEach((f: any) => {
          if (["heading", "paragraph", "total", "payment"].includes(f.type)) {
            return;
          }
          if (values[f.id] !== undefined) {
            cleanValues[f.id] = values[f.id];
          }
        });
      }
      const hpPayload: Record<string, string> = {};
      if (formData.spam?.honeypot?.field) {
        hpPayload[formData.spam.honeypot.field] = honeypotVal;
      }
      const submitPayload: any = {
        values: cleanValues,
        token: formData.spam?.token,
        hp: formData.spam?.honeypot ? hpPayload : undefined,
        meta: {
          source_url: typeof window !== "undefined" ? window.location.href : "",
          referrer: typeof document !== "undefined" ? document.referrer : ""
        }
      };
      const res = await client.pluginRequest(`wvc-forms/v1/forms/${formData.key}/submit`, {
        method: "POST",
        data: submitPayload
      });
      if (res?.success) {
        if (res.confirmation?.type === "redirect" && res.confirmation?.url) {
          window.location.assign(res.confirmation.url);
        } else {
          setConfirmationHtml(res.confirmation?.message || "<h3>Quotation request received</h3><p>Thank you. Pete or our estimation team will review your project parameters and respond with a structured quotation promptly.</p>");
        }
      } else if (res?.code === "frms_validation_failed") {
        setFieldErrors(res.errors || {});
        setGlobalError(res.message || "Please complete all required fields correctly.");
      } else if (res?.code === "frms_invalid_token") {
        await loadFormDefinition();
        setGlobalError("Your form session timed out. The security token has refreshed; please re-submit.");
      } else {
        setGlobalError(res?.message || "Unable to submit your quotation request. Please check details and try again.");
      }
    } catch (err: any) {
      setGlobalError(err?.message || "Network error. Please try submitting again or phone our office directly.");
    } finally {
      setSubmitting(false);
    }
  };
  const nameField = getField("full_name") || getField("name");
  const companyField = getField("company_name") || getField("company");
  const emailField = getField("email") || getField("email_address");
  const phoneField = getField("phone") || getField("phone_number");
  const locationField = getField("project_location") || getField("location");
  const typeField = getField("project_type") || getField("service");
  const dateField = getField("start_date") || getField("estimated_start_date");
  const detailsField = getField("project_details") || getField("details") || getField("message");
  const photoField = getField("photos") || getField("photo_upload") || getField("images");
  const docField = getField("drawings") || getField("document_upload") || getField("files");
  const contactMethodField = getField("contact_method") || getField("preferred_contact");
  const projectTypeOptions = typeField?.options && typeField.options.length > 0 ? typeField.options : [{
    value: "commercial",
    label: "Commercial"
  }, {
    value: "residential",
    label: "Residential"
  }, {
    value: "roofing",
    label: "Roofing"
  }, {
    value: "solar",
    label: "Solar"
  }, {
    value: "temporary_roofs",
    label: "Temporary Roofs"
  }, {
    value: "new_build",
    label: "New Build"
  }, {
    value: "industrial",
    label: "Industrial"
  }, {
    value: "specialist_access",
    label: "Specialist Access"
  }, {
    value: "refurbishment",
    label: "Refurbishment"
  }, {
    value: "edge_protection",
    label: "Edge Protection"
  }, {
    value: "chimney",
    label: "Chimney"
  }];
  const contactMethodOptions = contactMethodField?.options && contactMethodField.options.length > 0 ? contactMethodField.options : [{
    value: "phone",
    label: "Phone"
  }, {
    value: "email",
    label: "Email"
  }];
  return <section id="quote-request-form" data-nav="light" className="relative w-full bg-background py-16 text-foreground md:py-24">
      <div className="mx-auto max-w-4xl px-5 sm:px-8 md:px-12 lg:px-14">
        <div className="mb-10 max-w-3xl">
          <div className="mb-3 flex items-center gap-2">
            <span className="h-1 w-8 rounded-full bg-primary" />
            <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
              Commercial &amp; Residential Scaffolding
            </span>
          </div>
          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
            Tell us about your project
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            Send us the project location and any useful details, photographs or drawings and the BMF team will review your enquiry and come back to you.
          </p>
        </div>

        <motion.div initial={prefersReduced ? false : {
        opacity: 0,
        y: 18
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.1
      }} transition={{
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }} className="w-full">
          <Card className="border border-border bg-card shadow-sm">
            <CardHeader className="border-b border-border pb-6">
              <CardTitle className="font-default text-xl font-bold text-card-foreground sm:text-2xl">
                Project Specification Details
              </CardTitle>
              <CardDescription className="text-sm text-muted-foreground">
                Complete the fields below. Pete or our team will review access logistics and prepare a detailed estimate.
              </CardDescription>
            </CardHeader>

            <CardContent className="p-6 sm:p-8">
              {loadingForm ? <div className="flex flex-col items-center justify-center py-16 text-center">
                  <Loader2 className="size-8 animate-spin text-primary" />
                  <p className="mt-4 text-sm font-medium text-muted-foreground">
                    Initialising secure quotation form...
                  </p>
                </div> : formLoadError ? <Alert variant="destructive" className="border-destructive/30">
                  <AlertCircle className="size-5" />
                  <AlertTitle>Form temporarily unavailable</AlertTitle>
                  <AlertDescription className="mt-2 text-sm">
                    We were unable to load the interactive quotation form. Please contact our office directly at{" "}
                    <span className="font-semibold text-foreground">info@bmfscaffolding.co.uk</span> to discuss your project.
                    <div className="mt-4">
                      <Button variant="outline" size="sm" onClick={() => loadFormDefinition()} className="h-8 border-border text-xs">
                        Retry Loading
                      </Button>
                    </div>
                  </AlertDescription>
                </Alert> : confirmationHtml ? <motion.div initial={{
              opacity: 0,
              scale: 0.98
            }} animate={{
              opacity: 1,
              scale: 1
            }} className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="mb-4 flex size-14 items-center justify-center rounded-full bg-accent text-chart-4">
                    <CheckCircle2 className="size-8" />
                  </div>
                  <div className="prose text-foreground max-w-lg text-sm sm:text-base leading-relaxed" dangerouslySetInnerHTML={{
                __html: confirmationHtml
              }} />
                  <div className="mt-8 flex flex-wrap justify-center gap-4">
                    <Button variant="outline" onClick={() => {
                  setConfirmationHtml(null);
                  loadFormDefinition();
                }}>
                      Submit another enquiry
                    </Button>
                  </div>
                </motion.div> : <form onSubmit={handleSubmit} className="space-y-6">
                  {formData.spam?.honeypot?.field && <div style={{
                position: "absolute",
                left: "-9999px",
                opacity: 0,
                pointerEvents: "none"
              }} aria-hidden="true">
                      <label htmlFor={formData.spam.honeypot.field}>Leave blank</label>
                      <input id={formData.spam.honeypot.field} type="text" name={formData.spam.honeypot.field} tabIndex={-1} autoComplete="off" value={honeypotVal} onChange={e => setHoneypotVal(e.target.value)} />
                    </div>}

                  {globalError && <Alert variant="destructive" className="border-destructive/30">
                      <AlertCircle className="size-4" />
                      <AlertTitle>Submission Attention</AlertTitle>
                      <AlertDescription>{globalError}</AlertDescription>
                    </Alert>}

                  {uploadError && <Alert variant="destructive" className="border-destructive/30">
                      <AlertCircle className="size-4" />
                      <AlertTitle>Upload notice</AlertTitle>
                      <AlertDescription>{uploadError}</AlertDescription>
                    </Alert>}

                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor={nameInputId} className="text-sm font-medium text-foreground">
                        {nameField?.label || "Full Name"} {nameField?.required && <span className="text-destructive">*</span>}
                      </Label>
                      <Input id={nameInputId} type="text" required={nameField?.required ?? true} placeholder={nameField?.placeholder || "e.g. Pete Wood"} value={nameField ? values[nameField.id] || "" : ""} onChange={e => nameField && handleInputChange(nameField.id, e.target.value)} className={`h-12 border-input bg-card px-4 ${nameField && fieldErrors[nameField.id] ? "border-destructive ring-1 ring-destructive" : ""}`} />
                      {nameField && fieldErrors[nameField.id] && <p className="text-xs text-destructive">{fieldErrors[nameField.id].message}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={companyInputId} className="text-sm font-medium text-foreground">
                        {companyField?.label || "Company Name"} {companyField?.required && <span className="text-destructive">*</span>}
                      </Label>
                      <Input id={companyInputId} type="text" required={companyField?.required ?? false} placeholder={companyField?.placeholder || "e.g. Apex Roofing Ltd"} value={companyField ? values[companyField.id] || "" : ""} onChange={e => companyField && handleInputChange(companyField.id, e.target.value)} className="h-12 border-input bg-card px-4" />
                      {companyField && fieldErrors[companyField.id] && <p className="text-xs text-destructive">{fieldErrors[companyField.id].message}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor={emailInputId} className="text-sm font-medium text-foreground">
                        {emailField?.label || "Email Address"} {emailField?.required && <span className="text-destructive">*</span>}
                      </Label>
                      <Input id={emailInputId} type="email" required={emailField?.required ?? true} placeholder={emailField?.placeholder || "name@company.co.uk"} value={emailField ? values[emailField.id] || "" : ""} onChange={e => emailField && handleInputChange(emailField.id, e.target.value)} className={`h-12 border-input bg-card px-4 ${emailField && fieldErrors[emailField.id] ? "border-destructive ring-1 ring-destructive" : ""}`} />
                      {emailField && fieldErrors[emailField.id] && <p className="text-xs text-destructive">{fieldErrors[emailField.id].message}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={phoneInputId} className="text-sm font-medium text-foreground">
                        {phoneField?.label || "Phone Number"} {phoneField?.required && <span className="text-destructive">*</span>}
                      </Label>
                      <Input id={phoneInputId} type="tel" required={phoneField?.required ?? true} placeholder={phoneField?.placeholder || "e.g. 07700 900077"} value={phoneField ? values[phoneField.id] || "" : ""} onChange={e => phoneField && handleInputChange(phoneField.id, e.target.value)} className={`h-12 border-input bg-card px-4 ${phoneField && fieldErrors[phoneField.id] ? "border-destructive ring-1 ring-destructive" : ""}`} />
                      {phoneField && fieldErrors[phoneField.id] && <p className="text-xs text-destructive">{fieldErrors[phoneField.id].message}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor={locationInputId} className="text-sm font-medium text-foreground">
                        {locationField?.label || "Project Location (Town / Postcode)"} {locationField?.required && <span className="text-destructive">*</span>}
                      </Label>
                      <Input id={locationInputId} type="text" required={locationField?.required ?? true} placeholder={locationField?.placeholder || "e.g. Reading, RG1 or Guildford, GU1"} value={locationField ? values[locationField.id] || "" : ""} onChange={e => locationField && handleInputChange(locationField.id, e.target.value)} className={`h-12 border-input bg-card px-4 ${locationField && fieldErrors[locationField.id] ? "border-destructive ring-1 ring-destructive" : ""}`} />
                      {locationField && fieldErrors[locationField.id] && <p className="text-xs text-destructive">{fieldErrors[locationField.id].message}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={projectTypeSelectId} className="text-sm font-medium text-foreground">
                        {typeField?.label || "Project Type"} {typeField?.required && <span className="text-destructive">*</span>}
                      </Label>
                      <Select value={typeField ? values[typeField.id] || "" : ""} onValueChange={val => typeField && handleInputChange(typeField.id, val)}>
                        <SelectTrigger id={projectTypeSelectId} className="h-12 w-full border-input bg-card px-4">
                          <SelectValue placeholder="Select project type" />
                        </SelectTrigger>
                        <SelectContent>
                          {projectTypeOptions.map((opt: any, idx: number) => <SelectItem key={opt.value || idx} value={opt.value}>
                              {opt.label}
                            </SelectItem>)}
                        </SelectContent>
                      </Select>
                      {typeField && fieldErrors[typeField.id] && <p className="text-xs text-destructive">{fieldErrors[typeField.id].message}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor={startDateInputId} className="text-sm font-medium text-foreground">
                        {dateField?.label || "Estimated Start Date"} {dateField?.required && <span className="text-destructive">*</span>}
                      </Label>
                      <Input id={startDateInputId} type="date" required={dateField?.required ?? false} value={dateField ? values[dateField.id] || "" : ""} onChange={e => dateField && handleInputChange(dateField.id, e.target.value)} className={`h-12 border-input bg-card px-4 ${dateField && fieldErrors[dateField.id] ? "border-destructive ring-1 ring-destructive" : ""}`} />
                      {dateField && fieldErrors[dateField.id] && <p className="text-xs text-destructive">{fieldErrors[dateField.id].message}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={preferredContactSelectId} className="text-sm font-medium text-foreground">
                        {contactMethodField?.label || "Preferred Contact Method"} {contactMethodField?.required && <span className="text-destructive">*</span>}
                      </Label>
                      <Select value={contactMethodField ? values[contactMethodField.id] || "" : ""} onValueChange={val => contactMethodField && handleInputChange(contactMethodField.id, val)}>
                        <SelectTrigger id={preferredContactSelectId} className="h-12 w-full border-input bg-card px-4">
                          <SelectValue placeholder="Select contact preference" />
                        </SelectTrigger>
                        <SelectContent>
                          {contactMethodOptions.map((opt: any, idx: number) => <SelectItem key={opt.value || idx} value={opt.value}>
                              {opt.label}
                            </SelectItem>)}
                        </SelectContent>
                      </Select>
                      {contactMethodField && fieldErrors[contactMethodField.id] && <p className="text-xs text-destructive">{fieldErrors[contactMethodField.id].message}</p>}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={detailsTextareaId} className="text-sm font-medium text-foreground">
                      {detailsField?.label || "Project Details & Access Requirements"} {detailsField?.required && <span className="text-destructive">*</span>}
                    </Label>
                    <Textarea id={detailsTextareaId} required={detailsField?.required ?? false} rows={4} placeholder={detailsField?.placeholder || "Describe elevation, number of lifts, pavement license requirements, parking constraints, or temporary roof needs..."} value={detailsField ? values[detailsField.id] || "" : ""} onChange={e => detailsField && handleInputChange(detailsField.id, e.target.value)} className={`min-h-[120px] border-input bg-card p-4 leading-relaxed ${detailsField && fieldErrors[detailsField.id] ? "border-destructive ring-1 ring-destructive" : ""}`} />
                    {detailsField && fieldErrors[detailsField.id] && <p className="text-xs text-destructive">{fieldErrors[detailsField.id].message}</p>}
                  </div>

                  <div className="grid grid-cols-1 gap-6 pt-2 sm:grid-cols-2">
                    <div className="rounded-lg border border-border bg-accent/40 p-4">
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <Label htmlFor={photoUploadId} className="text-sm font-semibold text-foreground">
                            {photoField?.label || "Site Photo Upload"}
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            Attach site views, building elevations, or street access photos (JPG, PNG).
                          </p>
                        </div>
                        <Upload className="size-5 text-muted-foreground" />
                      </div>
                      <div className="mt-3">
                        <input id={photoUploadId} type="file" multiple accept="image/*" disabled={uploadingField !== null} onChange={e => photoField && handleFileUpload(e, photoField.id)} className="text-xs text-muted-foreground file:mr-3 file:rounded-md file:border file:border-border file:bg-card file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-foreground hover:file:bg-accent" />
                      </div>
                      {photoField && <div className="mt-3 space-y-1.5">
                          {uploadedFiles.filter(f => f.fieldId === photoField.id).map((f, i) => <div key={f.fileId} data-index={i} className="flex items-center justify-between rounded border border-border bg-card px-2.5 py-1.5 text-xs text-card-foreground">
                                <span className="truncate pr-2 font-medium">{f.name}</span>
                                <Button type="button" variant="ghost" size="icon" onClick={() => handleRemoveFile(photoField.id, f.fileId)} className="size-auto p-0 text-muted-foreground hover:bg-transparent hover:text-destructive" aria-label={`Remove ${f.name}`}>
                                  <X className="size-3.5" />
                                </Button>
                              </div>)}
                        </div>}
                    </div>

                    <div className="rounded-lg border border-border bg-accent/40 p-4">
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <Label htmlFor={docUploadId} className="text-sm font-semibold text-foreground">
                            {docField?.label || "Architectural Drawings & Documents"}
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            Upload architectural plans, structural specs, or site drawings (PDF, DWG).
                          </p>
                        </div>
                        <FileText className="size-5 text-muted-foreground" />
                      </div>
                      <div className="mt-3">
                        <input id={docUploadId} type="file" multiple accept=".pdf,.doc,.docx,.dwg" disabled={uploadingField !== null} onChange={e => docField && handleFileUpload(e, docField.id)} className="text-xs text-muted-foreground file:mr-3 file:rounded-md file:border file:border-border file:bg-card file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-foreground hover:file:bg-accent" />
                      </div>
                      {docField && <div className="mt-3 space-y-1.5">
                          {uploadedFiles.filter(f => f.fieldId === docField.id).map((f, i) => <div key={f.fileId} data-index={i} className="flex items-center justify-between rounded border border-border bg-card px-2.5 py-1.5 text-xs text-card-foreground">
                                <span className="truncate pr-2 font-medium">{f.name}</span>
                                <Button type="button" variant="ghost" size="icon" onClick={() => handleRemoveFile(docField.id, f.fileId)} className="size-auto p-0 text-muted-foreground hover:bg-transparent hover:text-destructive" aria-label={`Remove ${f.name}`}>
                                  <X className="size-3.5" />
                                </Button>
                              </div>)}
                        </div>}
                    </div>
                  </div>

                  <div className="pt-4">
                    <Button type="submit" disabled={submitting || uploadingField !== null} className="h-12 w-full rounded-md bg-primary px-8 font-default text-base font-semibold text-primary-foreground shadow-sm transition-[color,box-shadow,transform] hover:bg-primary/90 sm:w-auto">
                      {submitting ? <>
                          <Loader2 className="mr-2 size-4 animate-spin" />
                          Submitting quotation...
                        </> : formData.submit?.label || "Submit quote request"}
                    </Button>
                    <p className="mt-3 text-xs text-muted-foreground">
                      Your specifications are transmitted securely directly to BMF Scaffolding Ltd. No sales calls, just operational estimates.
                    </p>
                  </div>
                </form>}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>;
}